<script lang="ts">
  import Modal from '@shared/ui/Modal.svelte';
  import {
    validateProblemCreate,
    type ValidationIssue,
  } from '@domain/problem/validation';
  import { createProblem } from '@entities/problem/store.svelte';
  import ProblemCategorySelect from '@shared/ui/ProblemCategorySelect.svelte';
  import ProblemThemeSelect from '@shared/ui/ProblemThemeSelect.svelte';
  import type { Problem, ProblemCreateRequest } from '@shared/types/problem';

  interface Props {
    open: boolean;
    onClose: () => void;
    onCreated: (problem: Problem) => void;
  }

  let { open, onClose, onCreated }: Props = $props();

  let name = $state('');
  let category = $state('');
  let theme = $state('');
  // 旧 UI と同じく改行区切りの string_array として扱う。
  let detectionSignalsText = $state('');
  let detectionKeywords = $state('');

  let submitting = $state(false);
  let issues = $state<ValidationIssue[]>([]);
  let serverError = $state<string | null>(null);

  function reset() {
    name = '';
    category = '';
    theme = '';
    detectionSignalsText = '';
    detectionKeywords = '';
    issues = [];
    serverError = null;
  }

  function close() {
    reset();
    onClose();
  }

  function issueFor(field: keyof ProblemCreateRequest): string | undefined {
    return issues.find((i) => i.field === field)?.message;
  }

  function parseSignalLines(raw: string): string[] | null {
    const lines = raw
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    return lines.length > 0 ? lines : null;
  }

  async function handleSubmit(ev: SubmitEvent) {
    ev.preventDefault();
    if (submitting) return;
    const signals = parseSignalLines(detectionSignalsText);
    const payload: ProblemCreateRequest = {
      name: name.trim(),
      category: category.trim() || null,
      theme: theme.trim() || null,
      detection_signals: signals,
      detection_keywords: detectionKeywords.trim() || null,
    };
    issues = validateProblemCreate(payload);
    if (issues.length > 0) return;

    submitting = true;
    serverError = null;
    try {
      const problem = await createProblem(payload);
      onCreated(problem);
      reset();
    } catch (err) {
      serverError = err instanceof Error ? err.message : String(err);
    } finally {
      submitting = false;
    }
  }
</script>

<Modal {open} title="課題を追加" onClose={close}>
  <form id="problem-create-form" onsubmit={handleSubmit}>
    <label>
      <span>小課題名 <em>*</em></span>
      <input bind:value={name} required autocomplete="off" />
      {#if issueFor('name')}
        <small class="error">{issueFor('name')}</small>
      {/if}
    </label>
    <label>
      <span>大カテゴリ <em>*</em></span>
      <ProblemCategorySelect
        value={category}
        onChange={(v) => {
          category = v;
          theme = '';
        }}
        allowAdd={true}
        placeholderLabel="大カテゴリを選択"
      />
      {#if issueFor('category')}
        <small class="error">{issueFor('category')}</small>
      {/if}
    </label>
    <label>
      <span>中テーマ <em>*</em></span>
      <ProblemThemeSelect
        {category}
        value={theme}
        onChange={(v) => {
          theme = v;
        }}
        allowAdd={true}
        placeholderLabel="中テーマを選択"
      />
      {#if issueFor('theme')}
        <small class="error">{issueFor('theme')}</small>
      {/if}
    </label>
    <label>
      <span>推認属性</span>
      <textarea
        bind:value={detectionSignalsText}
        rows={3}
        autocomplete="off"
        placeholder={'例: ITリテラシー=低\n利用ITツール=Excel中心'}
      ></textarea>
      <small class="muted">1 行 1 属性。 改行で区切ってください。</small>
    </label>
    <label>
      <span>推認キーワード</span>
      <textarea
        bind:value={detectionKeywords}
        rows={3}
        autocomplete="off"
        placeholder="例: 手作業, Excel, 属人化 (カンマ or 改行区切り)"
      ></textarea>
    </label>
    {#if serverError}
      <p class="server-error">{serverError}</p>
    {/if}
  </form>
  {#snippet footer()}
    <button type="button" onclick={close} disabled={submitting}
      >キャンセル</button
    >
    <button
      type="submit"
      form="problem-create-form"
      class="primary"
      disabled={submitting}
    >
      {submitting ? '作成中…' : '作成'}
    </button>
  {/snippet}
</Modal>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  label {
    display: flex;
    flex-direction: column;
    font-size: 0.875rem;
    color: #374151;
    gap: 4px;
  }
  em {
    color: #dc2626;
    font-style: normal;
  }
  input,
  textarea {
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
  }
  textarea {
    resize: vertical;
  }
  small.error {
    color: #b91c1c;
  }
  small.muted {
    color: #6b7280;
  }
  .server-error {
    color: #b91c1c;
    margin: 8px 0 0;
  }
  button.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: white;
  }
  button.primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
</style>
